import { useEffect, useState } from 'react';
import { Outlet, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { InspectionModeProvider, useInspectionMode } from '../../context/InspectionModeContext';
import { PortalTourProvider } from '../../context/PortalTourContext';
import { PortalHelpProvider } from '../../context/PortalHelpContext';
import PortalTourOverlay from './PortalTourOverlay';
import PortalHelpBubble from './PortalHelpBubble';
// The persistent page-tour strip is retired; contextual Help remains via
// PortalTourHelpButton and PortalHelpBubble (both unchanged).
import PortalTourHelpButton from './PortalTourHelpButton';
import { InspectionBanner } from '../ui/inspection-banner';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Menu,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  UserPlus,
  Eye,
  Settings,
  Building2,
} from 'lucide-react';
import PortalNotificationsBell from './PortalNotificationsBell';
import {
  findActivePortalNavigationModule,
  getHeaderQuickActions,
  getPortalNavigationModules,
  isPortalNavItemActive,
} from '../../config/portalNavigation';
import PortalRouteAccessGuard from './PortalRouteAccessGuard';
import CommunicationsLauncher from './CommunicationsLauncher';
import CommunicationsWorkspaceDrawer from './CommunicationsWorkspaceDrawer';
import { CommunicationsWorkspaceProvider } from '../../context/CommunicationsWorkspaceContext';
import TenantBrandMark from '../shared/TenantBrandMark';
import { currentPublicBrand } from '../../lib/publicBrand';
import { buildPortalPeopleSearchUrl } from '../../lib/peopleHeaderSearch';

function PortalModuleNav({
  modules,
  location,
  onClick,
  collapsed = false,
  mobile = false,
}) {
  const activeModule = findActivePortalNavigationModule(location.pathname, location.search, modules);
  const [expandedModuleId, setExpandedModuleId] = useState(() => (
    activeModule?.id || modules.find((module) => module.items.length > 1)?.id || null
  ));
  const [openMenuModuleId, setOpenMenuModuleId] = useState(null);

  useEffect(() => {
    if (activeModule?.items?.length > 1) {
      setExpandedModuleId(activeModule.id);
    }
  }, [activeModule?.id, activeModule?.items?.length]);

  return (
    <nav
      className={`flex-1 min-h-0 overflow-y-auto ${collapsed ? 'px-1 py-3' : 'px-3 py-4'} space-y-1.5`}
      data-testid={mobile ? 'portal-mobile-module-nav' : 'portal-desktop-module-nav'}
    >
      {modules.map((module) => {
        const Icon = module.icon;
        const isModuleActive = activeModule?.id === module.id;
        const isExpanded = expandedModuleId === module.id && module.items.length > 1;

        if (collapsed) {
          if (module.items.length === 1) {
            const targetItem = module.items[0];
            return (
              <Link
                key={module.id}
                to={targetItem.href}
                onClick={onClick}
                title={module.label}
                className={`group relative mx-auto flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                  isModuleActive
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-text-muted hover:bg-[#F8FAFA] hover:text-text-primary'
                }`}
                data-testid={`nav-module-${module.id}`}
              >
                <Icon className="h-4.5 w-4.5" />
                <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md border border-[#E4E8EB] bg-white px-2.5 py-1 text-xs font-medium text-text-primary shadow-md group-hover:block group-focus-visible:block">
                  {module.label}
                </span>
              </Link>
            );
          }

          return (
            <DropdownMenu
              key={module.id}
              open={openMenuModuleId === module.id}
              onOpenChange={(open) => setOpenMenuModuleId(open ? module.id : null)}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title={module.label}
                  aria-label={module.label}
                  className={`group relative mx-auto flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    isModuleActive
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-muted hover:bg-[#F8FAFA] hover:text-text-primary'
                  }`}
                  data-testid={`nav-module-${module.id}`}
                >
                  <Icon className="h-4.5 w-4.5" />
                  <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded-md border border-[#E4E8EB] bg-white px-2.5 py-1 text-xs font-medium text-text-primary shadow-md group-hover:block group-focus-visible:block">
                    {module.label}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                sideOffset={8}
                className="w-56 rounded-xl border-[#E4E8EB] bg-white p-2 shadow-xl"
                data-testid={`nav-module-${module.id}-menu`}
              >
                <p className="px-2 pb-1 text-xs font-semibold text-text-primary">{module.label}</p>
                {module.items.map((item) => (
                  <DropdownMenuItem
                    key={item.href}
                    asChild
                    className="cursor-pointer rounded-lg px-2 py-2 text-sm font-medium text-text-muted focus:bg-[#F8FAFA] focus:text-text-primary"
                    data-testid={`nav-menu-${item.tourKey}`}
                  >
                    <Link to={item.href} onClick={onClick}>
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        if (module.items.length === 1) {
          const item = module.items[0];
          // A single global module entry can own multiple internal workspace
          // routes (for example People -> Applicants/Onboarding/Employees).
          // Highlight the module for any owned route, not only its launch href.
          const isActive = isModuleActive;
          return (
            <Link
              key={module.id}
              to={item.href}
              onClick={onClick}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                isActive
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-text-muted hover:bg-[#F8FAFA] hover:text-text-primary'
              }`}
              data-testid={`nav-module-${module.id}`}
              data-tour={`nav-${item.tourKey}`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{module.label}</span>
            </Link>
          );
        }

        return (
          <div key={module.id} className="space-y-1">
            <button
              type="button"
              onClick={() => setExpandedModuleId(isExpanded ? null : module.id)}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-semibold transition-colors ${
                isModuleActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-text-muted hover:bg-[#F8FAFA] hover:text-text-primary'
              }`}
              aria-expanded={isExpanded}
              data-testid={`nav-module-${module.id}`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="min-w-0 flex-1 truncate">{module.label}</span>
              <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
            {isExpanded ? (
              <div className="space-y-0.5 pl-6" data-testid={`nav-module-${module.id}-children`}>
                {module.items.map((item) => {
                  const isActive = isPortalNavItemActive(item, location.pathname, location.search);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={onClick}
                      className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-text-muted hover:bg-[#F8FAFA] hover:text-text-primary'
                      }`}
                      data-testid={`nav-${item.tourKey}`}
                      data-tour={`nav-${item.tourKey}`}
                    >
                      <item.icon className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

export default function PortalLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [headerSearch, setHeaderSearch] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('portal_sidebar_collapsed') === 'true';
  });

  const { user, logout, isAuditor, isAdmin, authHelpers } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ownerConsoleRoute = location.pathname === '/portal/careops-admin';
  const navModules = getPortalNavigationModules(user, authHelpers());
  const headerActions = getHeaderQuickActions(user, authHelpers());

  const roleLabel = user?.role === 'platform_owner'
    ? 'Platform Owner'
    : user?.role?.replace('_', ' ');
  const planLabel = user?.workspace_entitlements?.plan_name
    || user?.workspace_entitlements?.plan_id?.replace(/_/g, ' ')
    || 'Workspace';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleSidebarCollapse = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try {
      localStorage.setItem('portal_sidebar_collapsed', String(next));
    } catch {}
  };

  const OwnerConsoleShell = () => (
    <div className="caredock-platform-theme min-h-screen bg-[#F3FAFF]" data-testid="caredock-owner-shell">
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-[#CBE7F6] bg-white/90 px-4 py-3 backdrop-blur-xl lg:px-8">
        <div className="flex items-center gap-3">
          <img src="/caredock-wordmark.svg" alt="CareDock" className="h-9 w-auto max-w-[160px]" />
          <div>
            <p className="text-sm font-semibold text-[#0D274E]">CareDock Owner Console</p>
            <p className="text-xs text-text-muted">Platform controls, tenants and launch safety</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-primary text-white text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-sm font-medium text-text-primary sm:inline">{user?.name}</span>
              <ChevronDown className="h-4 w-4 text-text-muted" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="border-b border-[#E4E8EB] px-3 py-2">
              <p className="text-sm font-medium text-text-primary">{user?.name}</p>
              <p className="text-xs text-text-muted">{user?.email}</p>
              <p className="mt-1 text-xs font-medium text-primary">{roleLabel}</p>
            </div>
            <DropdownMenuItem onClick={handleLogout} className="text-error">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>
      <main className="px-4 py-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );

  const PortalContent = () => {
    const { isInspectionMode, enableInspectionMode } = useInspectionMode();
    const { orgSettings, orgName, orgLogoUrl } = useOrg();
    const legalName = orgSettings?.company_name || orgName;
    const platformBrand = currentPublicBrand();
    const workspaceSubtitle = `${planLabel.charAt(0).toUpperCase()}${planLabel.slice(1)} plan`;

    const runHeaderSearch = () => {
      const query = headerSearch.trim();
      if (!query) return;
      // Search the People workspace the admin is actually viewing, rather
      // than redirecting Applicants to Employees based only on department.
      const target = buildPortalPeopleSearchUrl(
        location.pathname, location.search, headerActions.searchHref, query
      );
      if (target) navigate(target);
    };

    const TenantMark = ({ size = 'md' }) => (
      <TenantBrandMark
        orgName={orgName}
        logoUrl={orgLogoUrl}
        className={size === 'lg' ? 'h-10 w-10' : 'h-9 w-9'}
      />
    );

    return (
      <div className="min-h-screen bg-[#F8FAFA]">
        <InspectionBanner />

        <aside
          className={`hidden lg:fixed lg:inset-y-0 lg:z-30 lg:flex lg:flex-col sidebar transition-all duration-200 ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}
        >
          <div className="flex h-full flex-col flex-grow bg-white border-r border-[#E4E8EB]">
            {platformBrand.isCareDock && !sidebarCollapsed && (
              <Link to="/portal/dashboard" className="flex items-center border-b border-[#e9eff3] px-5 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-[#008fd5]" data-testid="caredock-platform-brand">
                <img src={platformBrand.wordmark} alt="CareDock platform" className="h-7 w-auto max-w-[158px]" />
              </Link>
            )}
            <div className={`flex items-center border-b border-[#E4E8EB] ${sidebarCollapsed ? 'justify-center px-2 py-4' : 'gap-3 px-5 py-4'}`}>
              <TenantMark />
              {!sidebarCollapsed && (
                <div className="min-w-0 flex-1">
                  <span className="font-heading font-semibold text-text-primary block truncate">{orgName}</span>
                  <p className="text-[10px] text-text-muted -mt-0.5 truncate">{workspaceSubtitle}</p>
                </div>
              )}
              <button
                onClick={toggleSidebarCollapse}
                className={`ml-auto flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-[#F8FAFA] hover:text-text-primary transition ${sidebarCollapsed ? 'mx-auto' : ''}`}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              </button>
            </div>

            <PortalModuleNav modules={navModules} location={location} collapsed={sidebarCollapsed} />

            <div className={`border-t border-[#E4E8EB] ${sidebarCollapsed ? 'p-2' : 'p-3.5'}`}>
              <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3 px-1'}`}>
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary text-white text-xs">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                {!sidebarCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{user?.name}</p>
                    <p className="text-[10px] text-text-muted truncate capitalize">
                      {user?.department ? `${roleLabel} - ${user.department}` : roleLabel}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E4E8EB]">
          <div className="flex min-h-[64px] items-center justify-between gap-1 px-2 py-2 sm:gap-2 sm:px-4">
            <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-sidebar-btn">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 bg-white">
                  {platformBrand.isCareDock && (
                    <div className="border-b border-[#e9eff3] px-6 py-4" data-testid="caredock-mobile-platform-brand">
                      <img src={platformBrand.wordmark} alt="CareDock platform" className="h-7 w-auto max-w-[160px]" />
                    </div>
                  )}
                  <div className="flex items-center gap-3 px-6 py-5 border-b border-[#E4E8EB]">
                    <TenantMark size="lg" />
                    <div className="min-w-0">
                      <span className="block truncate font-heading font-semibold text-text-primary">{orgName}</span>
                      <p className="truncate text-[10px] text-text-muted">{workspaceSubtitle}</p>
                    </div>
                  </div>
                  <PortalModuleNav
                    modules={navModules}
                    location={location}
                    onClick={() => setSidebarOpen(false)}
                    mobile
                  />
                </SheetContent>
              </Sheet>
              {platformBrand.isCareDock
                ? <img src={platformBrand.wordmark} alt="CareDock platform" className="h-7 w-auto max-w-[116px] sm:max-w-[150px]" />
                : <TenantMark />}
              <span className="hidden min-w-0 flex-1 truncate font-heading text-sm font-semibold text-text-primary sm:block" title={orgName}>
                {orgName}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
              <PortalTourHelpButton />
              <CommunicationsLauncher compact />
              <PortalNotificationsBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-primary text-white text-xs">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <div className={sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}>
          <header className="hidden lg:flex sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E4E8EB] px-8 py-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex max-w-[360px] items-center gap-3">
                <TenantMark />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-text-primary">{orgName}</p>
                  <p className="truncate text-[11px] text-text-muted">{legalName} - {workspaceSubtitle}</p>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
                <input
                  type="search"
                  value={headerSearch}
                  onChange={(e) => setHeaderSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && headerSearch.trim()) {
                      runHeaderSearch();
                    }
                  }}
                  placeholder={location.pathname.startsWith('/portal/recruitment')
                    ? 'Search applicants — press Enter'
                    : location.pathname.startsWith('/portal/employees')
                      ? 'Search staff — press Enter'
                      : (headerActions.searchPlaceholder || 'Search employees — press Enter')}
                  aria-label="Search records in the current workspace"
                  className="pl-10 pr-20 py-2 w-80 bg-[#F8FAFA] border border-[#E4E8EB] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="portal-search"
                />
                <button type="button" onClick={runHeaderSearch}
                  className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg bg-white px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-700"
                  aria-label="Search records" data-testid="portal-search-submit">
                  Search
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <PortalTourHelpButton />
              {!isInspectionMode && headerActions.showInspectionMode && (
                <Button
                  variant="outline"
                  className="rounded-xl border-primary/30 text-primary hover:bg-primary/10"
                  onClick={enableInspectionMode}
                  data-testid="enter-inspection-mode-btn"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Inspection Mode
                </Button>
              )}
              {headerActions.primaryAction ? (
                <Link to={headerActions.primaryAction.href}>
                  <Button className="bg-primary hover:bg-primary-hover text-white rounded-xl" data-testid="portal-primary-action">
                    <headerActions.primaryAction.icon className="mr-2 h-4 w-4" />
                    {headerActions.primaryAction.label}
                  </Button>
                </Link>
              ) : headerActions.showAddEmployee && !isAuditor() ? (
                <Link to="/portal/recruitment">
                  <Button className="bg-primary hover:bg-primary-hover text-white rounded-xl" data-testid="add-employee-btn">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite Applicant
                  </Button>
                </Link>
              ) : null}
              <CommunicationsLauncher />
              <PortalNotificationsBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-primary text-white text-sm">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-text-primary">{user?.name}</span>
                    <ChevronDown className="h-4 w-4 text-text-muted" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b border-[#E4E8EB]">
                    <p className="text-sm font-medium text-text-primary">{user?.name}</p>
                    <p className="text-xs text-text-muted">{user?.email}</p>
                    <div className="mt-2 rounded-lg bg-[#F8FAFA] px-2 py-1.5 text-xs text-text-muted">
                      <p className="flex items-center gap-1 font-medium text-text-primary">
                        <Building2 className="h-3.5 w-3.5" />
                        {orgName}
                      </p>
                      <p className="mt-0.5 capitalize">{roleLabel} - {workspaceSubtitle}</p>
                    </div>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/portal/settings">
                      <Settings className="mr-2 h-4 w-4" />
                      Organisation Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-error">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          <main className="p-4 lg:p-8 main-content" data-tour="portal-main">
            <PortalRouteAccessGuard>
              <Outlet />
            </PortalRouteAccessGuard>
          </main>
        </div>
      </div>
    );
  };

  if (user?.role === 'platform_owner' && !ownerConsoleRoute) {
    return <Navigate to="/portal/careops-admin" replace />;
  }

  if (ownerConsoleRoute && user?.role !== 'platform_owner') {
    return <Navigate to="/portal/dashboard" replace />;
  }

  if (ownerConsoleRoute) {
    return (
      <InspectionModeProvider>
        <PortalTourProvider>
          <PortalHelpProvider>
            <OwnerConsoleShell />
          </PortalHelpProvider>
        </PortalTourProvider>
      </InspectionModeProvider>
    );
  }

  return (
    <InspectionModeProvider>
      <PortalTourProvider>
        <PortalHelpProvider>
          <CommunicationsWorkspaceProvider>
            <PortalContent />
            <CommunicationsWorkspaceDrawer />
            <PortalTourOverlay />
            <PortalHelpBubble />
          </CommunicationsWorkspaceProvider>
        </PortalHelpProvider>
      </PortalTourProvider>
    </InspectionModeProvider>
  );
}
